import { PERIODS_SEPARATORS } from '@/common/constants/periods';
import { REGEX_YEAR } from '@/common/constants/regex/dateRegex';

type TParsedPeriod = {
  raw: string;
  year: number;
  half: number;
  quarter: number;
  month: number;
};

type TPeriodParseContext = {
  raw: string;
  year: number;
  periodNumber: number;
};

const getPeriodParseContext = (value: string): TPeriodParseContext => {
  const normalizedValue = String(value);
  const parsedYear = Number(normalizedValue.slice(0, 4));

  return {
    raw: normalizedValue,
    year: Number.isFinite(parsedYear) ? parsedYear : 0,
    periodNumber: 0,
  };
};

const getNumberAfterSeparator = (value: string, separator: string): number => {
  const rawPeriodNumber = value.split(separator)[1];
  const parsedPeriodNumber = Number(rawPeriodNumber ?? 0);
  return Number.isFinite(parsedPeriodNumber) ? parsedPeriodNumber : 0;
};

abstract class BasePeriodParseStrategy {
  protected abstract readonly separator: string;

  isMatch(value: string): boolean {
    return value.includes(this.separator);
  }

  getPeriodNumber(value: string): number {
    return getNumberAfterSeparator(value, this.separator);
  }

  protected getBaseParsedPeriod({ raw, year }: TPeriodParseContext): TParsedPeriod {
    return {
      raw,
      year,
      half: 0,
      quarter: 0,
      month: 0,
    };
  }

  abstract toParsed(context: TPeriodParseContext): TParsedPeriod;
}

class YearPeriodParseStrategy extends BasePeriodParseStrategy {
  protected readonly separator = '';

  isMatch(value: string): boolean {
    return REGEX_YEAR.test(value);
  }

  getPeriodNumber(): number {
    return 0;
  }

  toParsed(context: TPeriodParseContext): TParsedPeriod {
    return this.getBaseParsedPeriod(context);
  }
}

class HalfYearPeriodParseStrategy extends BasePeriodParseStrategy {
  protected readonly separator = PERIODS_SEPARATORS.halfYear;

  toParsed(context: TPeriodParseContext): TParsedPeriod {
    const baseParsedPeriod = this.getBaseParsedPeriod(context);

    return {
      ...baseParsedPeriod,
      half: context.periodNumber,
    };
  }
}

class QuarterPeriodParseStrategy extends BasePeriodParseStrategy {
  protected readonly separator = PERIODS_SEPARATORS.quarter;

  toParsed(context: TPeriodParseContext): TParsedPeriod {
    const baseParsedPeriod = this.getBaseParsedPeriod(context);
    const quarter = context.periodNumber;

    return {
      ...baseParsedPeriod,
      half: quarter <= 2 ? 1 : 2,
      quarter,
    };
  }
}

class MonthPeriodParseStrategy extends BasePeriodParseStrategy {
  protected readonly separator = PERIODS_SEPARATORS.month;

  toParsed(context: TPeriodParseContext): TParsedPeriod {
    const baseParsedPeriod = this.getBaseParsedPeriod(context);
    const month = context.periodNumber;

    return {
      ...baseParsedPeriod,
      half: month <= 6 ? 1 : 2,
      quarter: Math.ceil(month / 3),
      month,
    };
  }
}

const PERIOD_PARSE_STRATEGIES: BasePeriodParseStrategy[] = [
  new YearPeriodParseStrategy(),
  new HalfYearPeriodParseStrategy(),
  new QuarterPeriodParseStrategy(),
  new MonthPeriodParseStrategy(),
];

const getParsedPeriod = (value: string): TParsedPeriod => {
  const periodContext = getPeriodParseContext(value);
  const periodParseStrategy =
    PERIOD_PARSE_STRATEGIES.find((strategy) => strategy.isMatch(periodContext.raw)) ?? YEAR_PERIOD_PARSE_STRATEGY;

  return periodParseStrategy.toParsed({
    ...periodContext,
    periodNumber: periodParseStrategy.getPeriodNumber(periodContext.raw),
  });
};

export const getSortedYearHalfQuarterMonthPeriods = (values: string[]): string[] => {
  return [...values].sort((firstValue, secondValue) => {
    const parsedFirstValue = getParsedPeriod(firstValue);
    const parsedSecondValue = getParsedPeriod(secondValue);

    return (
      parsedFirstValue.year - parsedSecondValue.year ||
      parsedFirstValue.half - parsedSecondValue.half ||
      parsedFirstValue.quarter - parsedSecondValue.quarter ||
      parsedFirstValue.month - parsedSecondValue.month
    );
  });
};
